import { quoteApi, quoteImageApi } from '@/main/api';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import { GridFileResponse } from '@/main/components/shared/fileGrid/cardFileGrid';
import FileGrid from '@/main/components/shared/fileGrid/fileGrid';
import ModalCarouselFile from '@/main/components/shared/fileGrid/modalCarouselFile/modalCarouselFile';
import { GetAllClaimImagesResquest } from '@/main/models/request';
import {
  QuoteHeaderAnswerInformationResponse,
  QuoteImageResponse
} from '@/main/models/response';
import HeaderAnswer from '@/main/screens/quotation/createAnswer/answeredParts/headerAnswer/headerAnswer';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { getTranslateByKey, isNullUndefined, isNullUndefinedEmpty } from '@/main/services/utils';
import { useEffect, useState } from 'react';
import * as S from "./styles"
import { Tab, Tabs } from '@mui/material';
import { toast } from 'react-toastify';
import { downloadFile } from '@/main/services/utilsFile';

const INITIAL_INDEX_CAROUSEL = 0;

interface ICreateAnswerImageScreen {
  readonly dataHeader: QuoteHeaderAnswerInformationResponse;
  readonly isLoadingHeader: boolean;
  readonly guidQuoteParams: string;
  readonly handleFinishTime: () => void;
  readonly isFinishedTime: boolean;
  readonly titleScreen: string;
}

const mapToGridFile = (file: QuoteImageResponse): GridFileResponse => {
  return {
    fileExtension: file.fileExtension,
    fileName: file.fileName,
    fileUrl: file.externalKey,
    guid: file.guid
  };
};

type ImageTabType = 'initial' | 'supplementary';

type ImageCategoryAvailability = {
  initial: boolean;
  supplementary: boolean;
};

export function CreateAnswerImageScreen({
  dataHeader,
  guidQuoteParams,
  handleFinishTime,
  isFinishedTime,
  isLoadingHeader,
  titleScreen
}: ICreateAnswerImageScreen) {
  const [dataGrid, setDataGrid] = useState<QuoteImageResponse[]>([]);
  const [formFilters, setFormFilters] = useState<any | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenCarousel, setIsOpenCarousel] = useState(false);
  const [currentIndexCarousel, setCurrentIndexCarousel] = useState(
    INITIAL_INDEX_CAROUSEL
  );
  const [activeTab, setActiveTab] = useState<ImageTabType>('initial');
  const [availability, setAvailability] = useState<ImageCategoryAvailability>({
    initial: true,
    supplementary: false
  });
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true);

  function fetchData(request: any) {
    setIsLoading(true);
    return quoteApi
      .getAllClaimImages(request)
      .then(response => response.data)
      .finally(() => {
        setIsLoading(false);
      });
  }

  async function getNextPageDataSearch() {
    if (!formFilters) return;

    const request: GetAllClaimImagesResquest = {
      ...formFilters,
      pageIndex: formFilters.pageIndex
        ? formFilters.pageIndex + 1
        : PaginationEnum.PageIndexIntial,
      includeInitialQuoteImages: activeTab === 'initial',
      includeComplementQuoteImages: activeTab === 'supplementary'
    };

    setFormFilters(request);
  }

  function openFileInModalCarousel(file: QuoteImageResponse) {
    setIsOpenCarousel(true);
    const findedIndex = dataGrid.findIndex(item => item.guid === file.guid);

    setCurrentIndexCarousel(
      isNullUndefined(findedIndex) || findedIndex < 0
        ? INITIAL_INDEX_CAROUSEL
        : findedIndex
    );
  }

  useEffect(() => {
    if (isNullUndefinedEmpty(guidQuoteParams)) return;

    setIsCheckingAvailability(true);

    const checkInitial = quoteApi.getAllClaimImages({
      guidQuote: guidQuoteParams,
      guidClaim: '',
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: 1,
      includeInitialQuoteImages: true,
      includeComplementQuoteImages: false
    });

    const checkSupplementary = quoteApi.getAllClaimImages({
      guidQuote: guidQuoteParams,
      guidClaim: '',
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: 1,
      includeInitialQuoteImages: false,
      includeComplementQuoteImages: true
    });

    Promise.all([checkInitial, checkSupplementary])
      .then(([initialRes, suppRes]) => {
        const hasInitial = !!initialRes.data.results?.length;
        const hasSupplementary = !!suppRes.data.results?.length;

        setAvailability({
          initial: hasInitial,
          supplementary: hasSupplementary
        });

        if (!hasInitial && hasSupplementary) {
          setActiveTab('supplementary');
        } else {
          setActiveTab('initial');
        }
      })
      .finally(() => {
        setIsCheckingAvailability(false);
      });
  }, [guidQuoteParams]);

  useEffect(() => {
    if (isNullUndefinedEmpty(guidQuoteParams)) return;
    setFormFilters({
      guidQuote: guidQuoteParams,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize10
    });
  }, [guidQuoteParams]);

  useEffect(() => {
    if (isNullUndefinedEmpty(guidQuoteParams)) return;

    setFormFilters({
      guidQuote: guidQuoteParams,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize10,
      includeInitialQuoteImages: activeTab === 'initial',
      includeComplementQuoteImages: activeTab === 'supplementary'
    });

    // reset grid when switching tab
    setDataGrid([]);
    setTotalRecords(0);
  }, [guidQuoteParams, activeTab]);

  const hasAnyImages = availability.initial || availability.supplementary;

  async function handleDownloadImage(
    imageUrl: string,
    passedImageRecord?: QuoteImageResponse
  ) {
    try {
      const imageRecord = passedImageRecord;

      if (!imageRecord || !imageRecord.guid) {
        toast.error(getTranslateByKey('information.AnErrorOccurred', true));
        return;
      }

      const guidFile = imageRecord.guid;

      const response = await quoteImageApi.getDownloadImage(guidFile);
      let base64Data = response.data.result.value;

      if (!base64Data) {
        toast.error(getTranslateByKey('information.AnErrorOccurred', true));
        return;
      }

      if (base64Data.includes('base64,')) {
        base64Data = base64Data.split('base64,')[1];
      }

      const fileName =
        imageRecord.fileName || `image-${guidFile.substring(0, 8)}.jpeg`;

      downloadFile(
        base64Data,
        fileName,
        `image/${imageRecord.fileExtension.toLowerCase()}`
      );
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <ContainerScreen title={titleScreen}>
      <HeaderAnswer
        code={dataHeader.code}
        claimNr={dataHeader.claimNumber}
        insurerName={dataHeader.insurerCompanyName}
        bodyshopName={dataHeader.bodyShopCompanyName}
        bodyshopContactName={dataHeader.bodyshopContactName}
        bodyshopPhoneNr={dataHeader.bodyshopPhoneNr}
        bodyshopEmail={dataHeader.bodyshopEmail}
        vehicle={dataHeader.vehicle}
        vin={dataHeader.vin}
        licensePlate={dataHeader.vehicleRegistration}
        deliveryAddress={dataHeader.deliveryAddress}
        isLoading={isLoadingHeader}
        deadlineAnswer={dataHeader.deadlineAnswer}
        handleFinishTime={handleFinishTime}
        isFinishedTime={isFinishedTime}
        guidQuote={guidQuoteParams}
        status={{
          id: dataHeader.idQuoteSuppItemStatus,
          name: dataHeader.statusName
        }}
        bodyContent={
          <S.GridWrapper>
            <S.BoxTabel>
            {!isCheckingAvailability && (
              <Tabs
                value={activeTab}
                onChange={(_, value: ImageTabType) => setActiveTab(value)}
              >
                {availability.initial && (
                  <Tab label={getTranslateByKey('information.initialImages')} value="initial" />
                )}

                {availability.supplementary && (
                  <Tab label={getTranslateByKey('information.supplementaryImages')} value="supplementary" />
                )}
              </Tabs>
            )}
            {!isCheckingAvailability && hasAnyImages && (
              <FileGrid<GetAllClaimImagesResquest, QuoteImageResponse>
                dataGrid={dataGrid}
                fetchData={fetchData}
                setDataGrid={setDataGrid}
                setTotalRecords={setTotalRecords}
                totalRecords={totalRecords}
                isLoading={isLoading}
                formFilters={formFilters}
                setIsLoading={setIsLoading}
                openFileInModalCarousel={openFileInModalCarousel}
                mapToGridFile={mapToGridFile}
                canDownload
                handleDownload={handleDownloadImage}
              />
            )}
            </S.BoxTabel>
          </S.GridWrapper>
        }
      />

      <ModalCarouselFile<QuoteImageResponse>
        mapToGridFile={mapToGridFile}
        dataGrid={dataGrid}
        isLoading={isLoading}
        totalRecords={totalRecords}
        open={isOpenCarousel}
        setOpen={setIsOpenCarousel}
        getNextPageDataSearch={getNextPageDataSearch}
        currentIndexCarousel={currentIndexCarousel}
        setCurrentIndexCarousel={setCurrentIndexCarousel}
        canDownload
        handleDownload={handleDownloadImage}
      />
    </ContainerScreen>
  );
}
